'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import PublicNav from '@/components/PublicNav';
import HomeFooter from '@/app/components/HomeFooter';
import {
  Briefcase,
  Search,
  MapPin,
  Clock,
  Star,
  ArrowRight,
  CheckCircle,
  Filter,
  ChevronDown,
  Wrench,
  Code2,
  Megaphone,
  BarChart3,
  Palette,
  FlaskConical,
  Truck,
  HeartPulse,
  Plus,
  BadgeCheck,
  Users,
  Zap,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Specialist {
  id: string;
  name: string;
  title: string;
  skills: string[];
  location: string;
  rate: string;
  rating: number;
  reviews: number;
  availability: 'Available' | 'Busy' | 'Open to offers';
  avatar: string;
  verified: boolean;
  category: string;
}

interface JobPost {
  id: string;
  title: string;
  company: string;
  location: string;
  type: 'Full-time' | 'Part-time' | 'Contract' | 'Freelance';
  budget: string;
  skills: string[];
  posted: string;
  category: string;
  urgent: boolean;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const SPECIALISTS: Specialist[] = [
  {
    id: 'sp-1',
    name: 'Marshall Machina',
    title: 'Full-Stack Engineer',
    skills: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
    location: 'Accra, Ghana',
    rate: '$45/hr',
    rating: 4.9,
    reviews: 38,
    availability: 'Available',
    avatar: 'AO',
    verified: true,
    category: 'Engineering',
  },
  {
    id: 'sp-2',
    name: 'Fatima Al-Rashid',
    title: 'Growth Marketing Strategist',
    skills: ['SEO', 'Paid Ads', 'Email Marketing', 'Analytics'],
    location: 'Nairobi, Kenya',
    rate: '$35/hr',
    rating: 4.8,
    reviews: 52,
    availability: 'Open to offers',
    avatar: 'FA',
    verified: true,
    category: 'Marketing',
  },
  {
    id: 'sp-3',
    name: 'Kwame Mensah',
    title: 'AgriTech Consultant',
    skills: ['Precision Farming', 'IoT Sensors', 'Supply Chain', 'Data Analysis'],
    location: 'Lagos, Nigeria',
    rate: '$60/hr',
    rating: 5.0,
    reviews: 21,
    availability: 'Available',
    avatar: 'KM',
    verified: true,
    category: 'Consulting',
  },
  {
    id: 'sp-4',
    name: 'Priya Nair',
    title: 'UI/UX Designer',
    skills: ['Figma', 'User Research', 'Prototyping', 'Design Systems'],
    location: 'Remote',
    rate: '$40/hr',
    rating: 4.7,
    reviews: 67,
    availability: 'Busy',
    avatar: 'PN',
    verified: false,
    category: 'Design',
  },
  {
    id: 'sp-5',
    name: 'Chidi Eze',
    title: 'Financial Analyst',
    skills: ['Financial Modelling', 'Valuation', 'Excel', 'Power BI'],
    location: 'Johannesburg, SA',
    rate: '$55/hr',
    rating: 4.9,
    reviews: 29,
    availability: 'Available',
    avatar: 'CE',
    verified: true,
    category: 'Finance',
  },
  {
    id: 'sp-6',
    name: 'Lena Müller',
    title: 'Biotech Research Scientist',
    skills: ['Lab Research', 'Clinical Trials', 'Data Interpretation', 'Grant Writing'],
    location: 'Cape Town, SA',
    rate: '$70/hr',
    rating: 4.8,
    reviews: 14,
    availability: 'Open to offers',
    avatar: 'LM',
    verified: true,
    category: 'Science',
  },
];

const JOB_POSTS: JobPost[] = [
  {
    id: 'job-1',
    title: 'Senior React Developer for HealthTech Startup',
    company: 'MedConnect Africa',
    location: 'Remote / Lagos',
    type: 'Contract',
    budget: '$3,000–$5,000/mo',
    skills: ['React', 'TypeScript', 'REST APIs'],
    posted: '2h ago',
    category: 'Engineering',
    urgent: true,
  },
  {
    id: 'job-2',
    title: 'Marketing Lead — AgriTech Platform Launch',
    company: 'GreenRoot Ltd',
    location: 'Nairobi, Kenya',
    type: 'Full-time',
    budget: '$2,000–$3,500/mo',
    skills: ['Digital Marketing', 'Brand Strategy', 'Community Building'],
    posted: '1d ago',
    category: 'Marketing',
    urgent: false,
  },
  {
    id: 'job-3',
    title: 'Supply Chain Consultant — 3-Month Engagement',
    company: 'TradeFlow Solutions',
    location: 'Remote',
    type: 'Freelance',
    budget: '$80/hr',
    skills: ['Logistics', 'ERP Systems', 'Process Optimisation'],
    posted: '3d ago',
    category: 'Consulting',
    urgent: false,
  },
  {
    id: 'job-4',
    title: 'Product Designer for Fintech App Redesign',
    company: 'PaySmart Inc.',
    location: 'Accra, Ghana',
    type: 'Part-time',
    budget: '$1,500–$2,500/mo',
    skills: ['Figma', 'Mobile UX', 'Design Systems'],
    posted: '5d ago',
    category: 'Design',
    urgent: false,
  },
];

const CATEGORIES = [
  { id: 'cat-eng', label: 'Engineering', icon: Code2, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { id: 'cat-mkt', label: 'Marketing', icon: Megaphone, color: 'text-orange-600 bg-orange-50 border-orange-200' },
  { id: 'cat-fin', label: 'Finance', icon: BarChart3, color: 'text-green-600 bg-green-50 border-green-200' },
  { id: 'cat-des', label: 'Design', icon: Palette, color: 'text-violet-600 bg-violet-50 border-violet-200' },
  { id: 'cat-sci', label: 'Science', icon: FlaskConical, color: 'text-teal-600 bg-teal-50 border-teal-200' },
  { id: 'cat-ops', label: 'Operations', icon: Wrench, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { id: 'cat-log', label: 'Logistics', icon: Truck, color: 'text-slate-600 bg-slate-50 border-slate-200' },
  { id: 'cat-hlt', label: 'Healthcare', icon: HeartPulse, color: 'text-pink-600 bg-pink-50 border-pink-200' },
];

const AVAILABILITY_COLORS: Record<string, string> = {
  Available: 'bg-green-100 text-green-700',
  Busy: 'bg-red-100 text-red-700',
  'Open to offers': 'bg-amber-100 text-amber-700',
};

const JOB_TYPE_COLORS: Record<string, string> = {
  'Full-time': 'bg-blue-100 text-blue-700',
  'Part-time': 'bg-violet-100 text-violet-700',
  Contract: 'bg-amber-100 text-amber-700',
  Freelance: 'bg-teal-100 text-teal-700',
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function WorkersPage() {
  const [activeTab, setActiveTab] = useState<'specialists' | 'jobs'>('specialists');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const filteredSpecialists = SPECIALISTS.filter((s) => {
    const matchesSearch =
      !searchQuery ||
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.skills.some((sk) => sk.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCat = !selectedCategory || s.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  const filteredJobs = JOB_POSTS.filter((j) => {
    const matchesSearch =
      !searchQuery ||
      j.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      j.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      j.skills.some((sk) => sk.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCat = !selectedCategory || j.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* ── Hero ── */}
      <section className="gradient-hero blob-primary py-16 lg:py-24 overflow-hidden">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-card border border-primary/20 rounded-full px-4 py-1.5 mb-6 card-shadow">
              <Briefcase size={14} className="text-primary" />
              <span className="text-sm font-semibold text-primary">Hire Specialists · Find Work · Build Teams</span>
            </div>
            <h1 className="text-hero-xl font-extrabold text-white mb-5 tracking-tight">
              The Right Talent for{' '}
              <span className="text-primary">Every Venture.</span>
            </h1>
            <p className="text-hero-sub text-white/70 mb-10 max-w-2xl mx-auto">
              Entrepreneurs post hiring needs. Specialists list their skills. Monezite connects the builders who make solutions real.
            </p>

            {/* Search */}
            <div className="relative max-w-2xl mx-auto mb-8">
              <div className="flex items-center bg-card border-2 border-border focus-within:border-primary rounded-xl card-shadow-md transition-colors duration-150 overflow-hidden">
                <Search size={20} className="ml-4 text-muted-foreground shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search skills, roles, or companies…"
                  className="flex-1 px-3 py-4 bg-transparent text-foreground placeholder:text-muted-foreground outline-none text-sm sm:text-base font-medium min-w-0"
                />
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap items-center justify-center gap-6">
              {[
                { icon: Users, label: '840+ Specialists', color: 'text-primary' },
                { icon: Briefcase, label: '320+ Open Roles', color: 'text-success' },
                { icon: Zap, label: 'Avg. 24h Match', color: 'text-warning' },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-2 text-white/80">
                  <stat.icon size={16} className={stat.color} />
                  <span className="text-sm font-semibold">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Category Strip ── */}
      <section className="bg-card border-b border-border py-5 overflow-x-auto">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex items-center gap-3 min-w-max">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-150 whitespace-nowrap ${
                !selectedCategory
                  ? 'bg-primary text-white border-primary' :'bg-muted text-muted-foreground border-border hover:border-primary/40'
              }`}
            >
              All Categories
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(selectedCategory === cat.label ? null : cat.label)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-150 whitespace-nowrap ${
                  selectedCategory === cat.label
                    ? 'bg-primary text-white border-primary'
                    : `${cat.color} hover:opacity-80`
                }`}
              >
                <cat.icon size={13} />
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main Content ── */}
      <section className="py-12 lg:py-16">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">

          {/* Tabs + Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center bg-muted rounded-xl p-1 w-fit">
              <button
                onClick={() => setActiveTab('specialists')}
                className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${
                  activeTab === 'specialists' ?'bg-card text-foreground card-shadow' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Find Specialists
              </button>
              <button
                onClick={() => setActiveTab('jobs')}
                className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${
                  activeTab === 'jobs' ?'bg-card text-foreground card-shadow' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Browse Jobs
              </button>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setFilterOpen((p) => !p)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-muted-foreground bg-card border border-border rounded-xl hover:border-primary/40 transition-colors duration-150 card-shadow"
              >
                <Filter size={14} />
                Filters
                <ChevronDown size={14} className={`transition-transform duration-200 ${filterOpen ? 'rotate-180' : ''}`} />
              </button>
              {activeTab === 'jobs' ? (
                <Link
                  href="/sign-up-login-screen?mode=signup"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-cyan-600 rounded-xl transition-all duration-150 active:scale-95 card-shadow"
                >
                  <Plus size={14} />
                  Post a Job
                </Link>
              ) : (
                <Link
                  href="/sign-up-login-screen?mode=signup"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-cyan-600 rounded-xl transition-all duration-150 active:scale-95 card-shadow"
                >
                  <Plus size={14} />
                  List Your Skills
                </Link>
              )}
            </div>
          </div>

          {/* Results count */}
          <p className="text-sm text-muted-foreground mb-6">
            {activeTab === 'specialists'
              ? `${filteredSpecialists.length} specialist${filteredSpecialists.length !== 1 ? 's' : ''} found`
              : `${filteredJobs.length} job${filteredJobs.length !== 1 ? 's' : ''} found`}
            {selectedCategory && <span className="ml-1">in <span className="font-semibold text-foreground">{selectedCategory}</span></span>}
          </p>

          {/* ── Specialists Grid ── */}
          {activeTab === 'specialists' && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredSpecialists.map((sp) => (
                <div
                  key={sp.id}
                  className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-4 card-shadow hover:card-shadow-md transition-all duration-200 hover:-translate-y-0.5"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shrink-0">
                        <span className="text-sm font-bold text-white">{sp.avatar}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-bold text-foreground">{sp.name}</p>
                          {sp.verified && <BadgeCheck size={14} className="text-primary shrink-0" />}
                        </div>
                        <p className="text-xs text-muted-foreground">{sp.title}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${AVAILABILITY_COLORS[sp.availability]}`}>
                      {sp.availability}
                    </span>
                  </div>

                  {/* Skills */}
                  <div className="flex flex-wrap gap-1.5">
                    {sp.skills.map((sk) => (
                      <span key={`${sp.id}-${sk}`} className="text-xs font-medium bg-muted text-muted-foreground px-2.5 py-1 rounded-full">
                        {sk}
                      </span>
                    ))}
                  </div>

                  {/* Meta */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <MapPin size={11} />
                      <span>{sp.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star size={11} className="text-warning fill-warning" />
                      <span className="font-semibold text-foreground">{sp.rating}</span>
                      <span>({sp.reviews})</span>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-1 border-t border-border">
                    <span className="text-sm font-bold text-primary">{sp.rate}</span>
                    <Link
                      href="/sign-up-login-screen?mode=signup"
                      className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-cyan-600 transition-colors duration-150"
                    >
                      Hire {sp.name.split(' ')[0]} <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              ))}

              {filteredSpecialists.length === 0 && (
                <div className="col-span-full flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
                  <Users size={36} className="opacity-20" />
                  <p className="text-sm font-medium">No specialists match your search</p>
                  <button onClick={() => { setSearchQuery(''); setSelectedCategory(null); }} className="text-xs text-primary hover:underline">
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Jobs List ── */}
          {activeTab === 'jobs' && (
            <div className="flex flex-col gap-4">
              {filteredJobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-card border border-border rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 card-shadow hover:card-shadow-md transition-all duration-200"
                >
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Briefcase size={20} className="text-primary" />
                  </div>

                  {/* Body */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-sm font-bold text-foreground">{job.title}</h3>
                      {job.urgent && (
                        <span className="text-[10px] font-bold bg-danger/10 text-danger px-2 py-0.5 rounded-full uppercase tracking-wide">
                          Urgent
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mb-3">
                      <span className="font-semibold text-foreground">{job.company}</span>
                      <div className="flex items-center gap-1"><MapPin size={10} />{job.location}</div>
                      <div className="flex items-center gap-1"><Clock size={10} />{job.posted}</div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${JOB_TYPE_COLORS[job.type]}`}>
                        {job.type}
                      </span>
                      {job.skills.map((sk) => (
                        <span key={`${job.id}-${sk}`} className="text-xs font-medium bg-muted text-muted-foreground px-2.5 py-1 rounded-full">
                          {sk}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Right */}
                  <div className="flex flex-col items-end gap-3 shrink-0">
                    <span className="text-sm font-bold text-primary">{job.budget}</span>
                    <Link
                      href="/sign-up-login-screen?mode=signup"
                      className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-primary hover:bg-cyan-600 rounded-xl transition-all duration-150 active:scale-95"
                    >
                      Apply Now <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              ))}

              {filteredJobs.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
                  <Briefcase size={36} className="opacity-20" />
                  <p className="text-sm font-medium">No jobs match your search</p>
                  <button onClick={() => { setSearchQuery(''); setSelectedCategory(null); }} className="text-xs text-primary hover:underline">
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="py-16 lg:py-20 bg-muted/40">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Simple Process</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground">How Hiring & Employment Works</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* For Employers */}
            <div className="bg-card border border-border rounded-2xl p-6 card-shadow-md">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Briefcase size={20} className="text-primary" />
              </div>
              <h3 className="text-base font-bold text-foreground mb-1">For Employers & Entrepreneurs</h3>
              <p className="text-sm text-muted-foreground mb-5">Build your team with verified specialists who understand startup challenges.</p>
              <ul className="space-y-3">
                {[
                  'Post a job or hiring request in minutes',
                  'Browse specialist profiles by skill and availability',
                  'Message candidates directly through Monezite',
                  'Hire with confidence — verified profiles only',
                ].map((step) => (
                  <li key={step} className="flex items-start gap-2.5 text-sm text-foreground">
                    <CheckCircle size={15} className="text-success shrink-0 mt-0.5" />
                    {step}
                  </li>
                ))}
              </ul>
              <Link
                href="/sign-up-login-screen?mode=signup"
                className="mt-6 flex items-center justify-center gap-2 px-5 py-3 bg-primary hover:bg-cyan-600 text-white text-sm font-semibold rounded-xl transition-all duration-150 active:scale-95"
              >
                Post a Job <ArrowRight size={14} />
              </Link>
            </div>

            {/* For Workers */}
            <div className="bg-card border border-border rounded-2xl p-6 card-shadow-md">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center mb-4">
                <Users size={20} className="text-violet-600" />
              </div>
              <h3 className="text-base font-bold text-foreground mb-1">For Specialists & Job Seekers</h3>
              <p className="text-sm text-muted-foreground mb-5">Get discovered by entrepreneurs solving real problems — work that matters.</p>
              <ul className="space-y-3">
                {[
                  'Create a free specialist profile in under 5 minutes',
                  'Set your availability, rate, and preferred work type',
                  'Get matched to relevant job posts automatically',
                  'Build your reputation with verified client reviews',
                ].map((step) => (
                  <li key={step} className="flex items-start gap-2.5 text-sm text-foreground">
                    <CheckCircle size={15} className="text-success shrink-0 mt-0.5" />
                    {step}
                  </li>
                ))}
              </ul>
              <Link
                href="/sign-up-login-screen?mode=signup"
                className="mt-6 flex items-center justify-center gap-2 px-5 py-3 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-xl transition-all duration-150 active:scale-95"
              >
                List Your Skills <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <HomeFooter />
    </div>
  );
}
